import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IoMdSend } from 'react-icons/io';
import { MUTATION } from '../../gql-operations';

export default function PublishedFolderComposer({ token, linkRole, allowAnonymousContributions, onAdded }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [body, setBody] = useState('');
    const [error, setError] = useState('');
    const request = useRef(null);
    const sending = useRef(false);
    const [add, { loading }] = useMutation(MUTATION.ADD_PUBLISHED_MESSAGE);
    const authenticated = Boolean(localStorage.getItem('token'));
    const canAdd = allowAnonymousContributions || (authenticated && ['ADD', 'EDIT'].includes(linkRole));
    // A read-only link does not advertise a compose action it cannot honor.
    if (!canAdd && (authenticated || !['ADD', 'EDIT'].includes(linkRole))) return null;
    const submit = async event => {
        event.preventDefault();
        if (!body.trim() || sending.current) return;
        if (!canAdd) { history.push(`/login?redirect=${encodeURIComponent(`/s/${token}`)}`); return; }
        sending.current = true;
        setError('');
        if (request.current?.body !== body || request.current?.token !== token) {
            request.current = { body, token, id: Array.from(window.crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('') };
        }
        try {
            await add({ variables: { data: { token, body, clientRequestId: request.current.id } } });
            setBody('');
            request.current = null;
            try { await onAdded?.(); } catch (_) { setError(t('folders:messageAddedRefreshFailed')); }
        } catch (failure) {
            setError(failure?.graphQLErrors?.some(item => item.extensions?.code === 'CONTRIBUTION_RATE_LIMIT')
                ? t('folders:contributionRateLimit') : t('folders:contributionFailed'));
        } finally { sending.current = false; }
    };
    return <Wrapper>
        <Form onSubmit={submit}>
            <Input value={body} disabled={loading} maxLength={10000} onChange={event => setBody(event.target.value)}
                placeholder={t('folders:addAMessage_1c5491f5')} aria-label={t('folders:addAMessage_1c5491f5')} />
            {body.trim() ? <Send type="submit" disabled={loading} aria-label={t('folders:sendMessage')}><IoMdSend size={24} /></Send> : null}
        </Form>
        {!authenticated && canAdd ? <Hint>{t('folders:anonymousContributionHint')}</Hint> : null}
        {error ? <Hint role="alert">{error}</Hint> : null}
    </Wrapper>;
}
const Wrapper = styled.div`flex: 0 0 auto; width: min(100%, 760px); margin: 10px auto 0;`;
const Form = styled.form`display: flex; align-items: center; gap: 6px; padding: 4px; border-radius: 12px; background: rgba(255,255,255,.08); box-shadow: inset 0 0 0 1px rgba(255,255,255,.11); backdrop-filter: saturate(160%) blur(10px);`;
const Input = styled.input`flex: 1; min-width: 0; min-height: 38px; border: 0; padding: 8px 10px; color: inherit; background: transparent; font: inherit; outline: none; &::placeholder { color: rgba(255,255,255,.42); }`;
const Send = styled.button`border: 0; border-radius: 8px; padding: 8px; background: transparent; color: inherit; display: flex; cursor: pointer; &:hover { background: rgba(255,255,255,.1); } &:disabled { opacity: .4; }`;
const Hint = styled.div`font-size: 12px; opacity: .6; margin-top: 6px;`;
